import React, { useCallback, useContext, useEffect, useState } from 'react';
import { MdOutlineLightMode, MdOutlineDarkMode } from 'react-icons/md';
import { Switch, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import { lightTheme, darkTheme } from '@styles/themes';
import PortalSpinner from '@components/PortalSpinner';
import { DefaultTheme } from 'styled-components';

import * as S from './styles';

interface SwitchThemeProps {
  onClick?: () => void;
  className?: string;
}

const SwitchTheme = ({ onClick, className }: SwitchThemeProps) => {
  const { t } = useTranslation('main');
  const { theme, setTheme, themeWithMainColor } = useContext(SessionContext);
  const isDarkTheme = theme?.title === 'dark';
  const [themeIsChanging, setThemeIsChanging] = useState(false);

  const toggleTheme = useCallback(() => {
    setThemeIsChanging(true);
    const newTheme: DefaultTheme = isDarkTheme ? lightTheme : darkTheme;
    setTheme(themeWithMainColor(newTheme));
  }, [isDarkTheme, setTheme, themeWithMainColor]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (themeIsChanging) {
      timer = setTimeout(() => {
        setThemeIsChanging(false);
      }, 1 * 600);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [themeIsChanging]);

  if (themeIsChanging) {
    return <PortalSpinner />;
  }

  return (
    <S.SwitchThemeContainer className={className} onClick={onClick}>
      <span>{t(isDarkTheme ? 'dark' : 'light')}</span>
      <Tooltip title={t(`switchTo${isDarkTheme ? 'Light' : 'Dark'}Theme`)}>
        <Switch
          onChange={toggleTheme}
          checked={isDarkTheme}
          checkedChildren={<MdOutlineLightMode />}
          unCheckedChildren={<MdOutlineDarkMode />}
        />
      </Tooltip>
    </S.SwitchThemeContainer>
  );
};

export default SwitchTheme;
